import { useState, useEffect } from "react";
//import { useForm } from "react-hook-form";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Box,
    Stack,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    Typography,
} from "@mui/material";
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
} from "@mui/icons-material";
import { useCimbeo } from '../../../../context';

function UserManagement() {
    const { allUsers = [], setAllUsers, projects = [], setProjects } = useCimbeo();
    const [users, setUsers] = useState(allUsers || []);
    const [userDialogOpen, setUserDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [formData, setFormData] = useState({
        username: "",
        realName: "",
        organization: "",
        email: "",
        role: "",
        isDBUser: false,
        projectIds: []
    });

    // Available roles for users
    const availableRoles = ["Admin", "Manager", "User", "Developer", "Analyst"];
    
    // Get project titles from CIMBEO context with proper null handling
    const availableProjects = projects ? projects.map(project => ({
        id: project.id,
        title: project.title
    })) : [];
    
    // Dummy data for initial load if needed
    useEffect(() => {
        if (!allUsers || allUsers.length === 0) {
            const dummyUsers = [
                {
                    id: 1,
                    username: "admin",
                    realName: "Admin User",
                    organization: "Cimbeo",
                    email: "admin@example.com",
                    role: "Admin",
                    isDBUser: true,
                    projectIds: [1, 2]
                }
            ];
            
            if (setAllUsers) {
                setAllUsers(dummyUsers);
            }
            
            setUsers(dummyUsers);
        }
    }, [allUsers, setAllUsers]);

    // Initialize projects if empty
    useEffect(() => {
        if (!projects || projects.length === 0) {
            if (setProjects) {
                const dummyProjects = [
                    {
                        id: 1,
                        title: "Demo Project",
                        description: "A sample project for demonstration",
                        requirements: [
                            { title: "Requirement 1", description: "Sample requirement description" }
                        ]
                    },
                    {
                        id: 2,
                        title: "Test Project",
                        description: "A project for testing purposes",
                        requirements: [
                            { title: "Test Requirement", description: "Test requirement description" }
                        ]
                    }
                ];
                setProjects(dummyProjects);
            }
        }
    }, [projects, setProjects]);

    // Update local state when context changes
    useEffect(() => {
        setUsers(allUsers || []);
    }, [allUsers]);

    const handleUserDialogOpen = (user = null) => {
        if (user) {
            setSelectedUser(user);
            setFormData({
                username: user.username,
                realName: user.realName,
                organization: user.organization,
                email: user.email,
                role: user.role,
                isDBUser: user.isDBUser,
                projectIds: user.projectIds || []
            });
        } else {
            setSelectedUser(null);
            setFormData({
                username: "",
                realName: "",
                organization: "",
                email: "",
                role: "",
                isDBUser: false,
                projectIds: []
            });
        }
        setUserDialogOpen(true);
    };

    const handleUserDialogClose = () => {
        setUserDialogOpen(false);
        setSelectedUser(null);
    };

    const handleFormChange = (field) => (event) => {
        setFormData(prev => ({
            ...prev,
            [field]: field === 'isDBUser' ? event.target.checked : event.target.value
        }));
    };

    const handleDeleteUser = (userId) => {
        const updatedUsers = users.filter(user => user.id !== userId);
        setUsers(updatedUsers);
        setAllUsers(updatedUsers);
        // Update local storage
        localStorage.setItem('allUsers', JSON.stringify(updatedUsers));
    };

    const handleSaveUser = () => {
        let updatedUsers;
        if (selectedUser) {
            // Update existing user
            updatedUsers = users.map(user => 
                user.id === selectedUser.id 
                    ? { ...user, ...formData }
                    : user
            );
        } else {
            // Add new user
            const newUser = {
                id: Date.now(), // Use timestamp as unique ID
                ...formData
            };
            updatedUsers = [...users, newUser];
        }
        
        // Update both local state and context
        setUsers(updatedUsers);
        setAllUsers(updatedUsers);
        // Update local storage
        localStorage.setItem('allUsers', JSON.stringify(updatedUsers));
        handleUserDialogClose();
    };

    const getProjectTitle = (projectId) => {
        const project = projects ? projects.find(p => p.id === projectId) : null;
        return project ? project.title : 'Unknown Project';
    };

    return (
        <>
            <TableContainer>
                <Table>
                    <TableHead sx={{ display: "table-header-group" }}>
                        <TableRow>
                            <TableCell sx={{ color: "text.main" }}>Username</TableCell>
                            <TableCell sx={{ color: "text.main" }}>Real Name</TableCell>
                            <TableCell sx={{ color: "text.main" }}>Organization</TableCell>
                            <TableCell sx={{ color: "text.main" }}>Email</TableCell>
                            <TableCell sx={{ color: "text.main" }}>Role</TableCell>
                            <TableCell sx={{ color: "text.main" }}>DB User</TableCell>
                            <TableCell sx={{ color: "text.main" }}>Projects</TableCell>
                            <TableCell sx={{ color: "text.main" }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {users.map((user) => (
                            <TableRow key={user.id}>
                                <TableCell>{user.username}</TableCell>
                                <TableCell>{user.realName}</TableCell>
                                <TableCell>{user.organization}</TableCell>
                                <TableCell>{user.email}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={user.role}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Typography variant="text1">
                                        {user.isDBUser ? 'Yes' : '-'}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Stack 
                                        direction="row" 
                                        flexWrap="wrap"
                                        sx={{ 
                                            gap: '8px !important',
                                            '& > *': {
                                                margin: '0 !important'
                                            }
                                        }}
                                    >
                                        {(user.projectIds || []).map((projectId) => (
                                            <Chip
                                                key={projectId}
                                                label={getProjectTitle(projectId)}
                                                size="small"
                                            />
                                        ))}
                                    </Stack>
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    <IconButton 
                                        onClick={() => handleUserDialogOpen(user)}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton 
                                        color="error"
                                        onClick={() => handleDeleteUser(user.id)}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box sx={{ 
                mt: 2,
                display: 'flex',
                justifyContent: 'flex-end'
            }}>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleUserDialogOpen()}
                >
                    Add User
                </Button>
            </Box>

            {/* User Dialog */}
            <Dialog 
                open={userDialogOpen} 
                onClose={handleUserDialogClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {selectedUser ? "Edit User" : "Add User"}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 2 }}>
                        <TextField
                            autoFocus
                            label="Username"
                            fullWidth
                            value={formData.username}
                            onChange={handleFormChange('username')}
                            sx={{
                                '& .MuiInputBase-root': {
                                    height: '56px',
                                }
                            }}
                        />
                        <TextField
                            label="Real Name"
                            fullWidth
                            value={formData.realName}
                            onChange={handleFormChange('realName')}
                            sx={{
                                '& .MuiInputBase-root': {
                                    height: '56px',
                                }
                            }}
                        />
                        <TextField
                            label="Organization"
                            fullWidth
                            value={formData.organization}
                            onChange={handleFormChange('organization')}
                            sx={{
                                '& .MuiInputBase-root': {
                                    height: '56px',
                                }
                            }}
                        />
                        <TextField
                            label="Email"
                            type="email"
                            fullWidth
                            value={formData.email}
                            onChange={handleFormChange('email')}
                            sx={{
                                '& .MuiInputBase-root': {
                                    height: '56px',
                                }
                            }}
                        />
                        <FormControl fullWidth>
                            <InputLabel id="role-label">Role</InputLabel>
                            <Select
                                labelId="role-label"
                                label="Role"
                                value={formData.role}
                                onChange={handleFormChange('role')}
                                sx={{
                                    height: '56px'
                                }}
                            >
                                {availableRoles.map((role) => (
                                    <MenuItem key={role} value={role}>
                                        {role}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            gap: 1
                        }}>
                            <Switch
                                checked={formData.isDBUser}
                                onChange={handleFormChange('isDBUser')}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                        color: '#2B5587',
                                    },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                        backgroundColor: '#2B5587',
                                    },
                                }}
                            />
                            <Typography>DB User</Typography>
                        </Box>
                        <FormControl fullWidth>
                            <InputLabel id="projects-label">Projects</InputLabel>
                            <Select
                                labelId="projects-label"
                                label="Projects"
                                multiple
                                value={formData.projectIds}
                                onChange={handleFormChange('projectIds')}
                                IconComponent={() => (
                                    <AddIcon 
                                        style={{
                                            position: 'absolute',
                                            right: 8,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            color: '#2B5587',
                                            fontSize: 24,
                                            pointerEvents: 'none'
                                        }}
                                    />
                                )}
                                renderValue={(selected) => (
                                    <Box sx={{ 
                                        display: 'flex', 
                                        flexWrap: 'wrap', 
                                        gap: '8px'
                                    }}>
                                        {selected.map((projectId) => (
                                            <Chip
                                                key={projectId}
                                                label={getProjectTitle(projectId)}
                                                size="small"
                                            />
                                        ))}
                                    </Box>
                                )}
                                sx={{
                                    minHeight: '56px',
                                    '& .MuiSelect-select': {
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 0.5,
                                        minHeight: '56px',
                                        alignItems: 'center'
                                    }
                                }}
                            >
                                {availableProjects.map((project) => (
                                    <MenuItem key={project.id} value={project.id}>
                                        {project.title}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleUserDialogClose}>Cancel</Button>
                    <Button 
                        variant="contained" 
                        onClick={handleSaveUser}
                        sx={{ bgcolor: '#2B5587', color: '#FFFFFF' }}
                    >
                        {selectedUser ? "Save Changes" : "Add User"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default UserManagement; 